import {
  isCenterpiece,
  FRAME_COLORS,
  MATERIAL_TYPES,
} from '@mixtiles/web-backend-shared'
import { useBulkCustomization } from '../../components/BulkCustomizationProvider/BulkCustomizationProvider'
import { useTiles } from '../useTiles'
import { analytics, EVENT_NAMES } from '../../services/Analytics/Analytics'
import { getSizeName } from '../../services/SizesManager'
import { translateManager as t } from '../../services/TranslateManager'
import { getFrameName, getTileStyleData } from '../../services/tilesStyling'
import { useAvailableFrames } from '../../components/FrameProvider/AvailableStockProvider'
import { closeSnackbar } from 'notistack'
import {
  useOutOfStockNotification,
  OUT_OF_STOCK_NOTIFICATION_TYPE,
  OUT_OF_STOCK_PROPERTY,
} from '../../elements/Snackbar/OutOfStockNotification/useOutOfStockNotification'
import uniq from 'lodash/uniq'
import { useTilesRefs } from '../../components/TilesRefsProvider/TilesRefsProvider'
import { isFramedMaterial } from '../../utils/materialTypeUtils'
import {
  MIXED_COLORS,
  MIXED_MATERIALS,
  OUT_OF_STOCK,
} from '../../pages/PhotoStyler/TileDesignerConsts'
import useDialog from '../../elements/Dialog/DialogProvider'
import BulkOverrideWarningDialog from '../../pages/PhotoStyler/BulkOverrideWarningDialog/BulkOverrideWarningDialog'
import { TILE_CUSTOMIZATION } from '../../pages/PhotoStyler/tileCustomization.consts'
import {
  AVAILABLE_MATERIALS,
  materialsAsColorManager,
} from '../../services/MaterialsAsColorManager/MaterialsAsColorManager'

const FRAMED_FIRST_MATERIALS = [
  MATERIAL_TYPES.CLASSIC,
  MATERIAL_TYPES.WIDE_FRAME,
  MATERIAL_TYPES.FRAMELESS,
  MATERIAL_TYPES.CANVAS,
]

function useTileMaterial() {
  const { applyTileMaterial, applyTilesMaterial, tiles, getTile } = useTiles()
  const { saveTilesState, undoTiles, getRef, setTileMaterialToActualMaterial } =
    useTilesRefs()
  const { bulkCustomization, updateBulk } = useBulkCustomization()
  const {
    isFrameSupportedForSize,
    isSupportedSizeForMaterial,
    getSupportedFramesForSize,
  } = useAvailableFrames()
  const { notifyOutOfStock } = useOutOfStockNotification()
  const { getSizesInStockForMaterial } = useAvailableFrames()
  const dialog = useDialog()

  const supportedMaterials = materialsAsColorManager.isEnabled
    ? AVAILABLE_MATERIALS
    : FRAMED_FIRST_MATERIALS

  const updateTileMaterial = async (
    tileId,
    newMaterial,
    ignoreSupportCheck = false
  ) => {
    const tile = getTile(tileId)
    const isTileSupported = ignoreSupportCheck
      ? true
      : !isUnsupportedTileForMaterial({
          tile,
          newMaterial,
        })
    const prevBulkCustomization = { ...bulkCustomization }

    if (!isTileSupported) {
      saveTilesState()
    }

    applyTileMaterial(tileId, newMaterial)

    await verifyStock({
      unsupportedTiles: isTileSupported ? [] : [tile],
      newMaterial,
      prevBulkCustomization,
    })
  }

  const updateBulkMaterial = async (
    newMaterial,
    shouldIgnoreUnsupportedColor = false,
    skipDialog = false,
    onDone,
    currentColor
  ) => {
    const currentBulkMaterial = bulkCustomization.materialType
    if (
      (currentBulkMaterial === MIXED_MATERIALS ||
        currentColor === MIXED_COLORS) &&
      !skipDialog
    ) {
      dialog.open(
        <BulkOverrideWarningDialog
          open
          onDismiss={() => {
            onDone?.(false)
            dialog.close()
          }}
          onApproved={() =>
            updateMaterialAndNotify(
              newMaterial,
              shouldIgnoreUnsupportedColor
            ) && onDone?.(true)
          }
          ctaText={t.get('designer.bulk_override_warning.apply_to_all')}
          customization={
            materialsAsColorManager.isEnabled
              ? TILE_CUSTOMIZATION.FRAME_COLOR
              : TILE_CUSTOMIZATION.MATERIAL
          }
        />
      )
    } else {
      setTileMaterialToActualMaterial(currentBulkMaterial)
      await updateMaterialAndNotify(newMaterial, shouldIgnoreUnsupportedColor)
      onDone?.(true)
    }
  }

  const updateMaterialAndNotify = async (
    newMaterial,
    shouldIgnoreUnsupportedColor
  ) => {
    const unsupportedTilesForMaterial = shouldIgnoreUnsupportedColor
      ? []
      : getUnsupportedTilesForMaterial({
          newMaterial,
        })

    const prevBulkCustomization = { ...bulkCustomization }

    if (unsupportedTilesForMaterial.length > 0) {
      saveTilesState()
    }

    updateBulk.materialType(newMaterial)
    applyTilesMaterial(newMaterial)

    await verifyStock({
      unsupportedTiles: unsupportedTilesForMaterial,
      newMaterial,
      prevBulkCustomization,
    })
  }

  const verifyStock = async ({
    unsupportedTiles,
    newMaterial,
    prevBulkCustomization,
  }) => {
    if (unsupportedTiles.length > 0) {
      await new Promise((resolve) => setTimeout(resolve, 300))
      const ref = getRef({ tileId: unsupportedTiles[0].id })
      if (ref.current && globalThis.scrollIntoView) {
        globalThis.scrollIntoView(ref.current, {
          behavior: 'smooth',
          scrollMode: 'always',
          inline: 'center',
        })
      }
      notifyMaterialInSizeUnavailable({
        material: newMaterial,
        prevBulkCustomization,
        unsupportedTiles,
      })
    }
  }

  const getSnackbarTitles = ({
    unsupportedTiles,
    unsupportedUsedTileSizes,
    material,
  }) => {
    if (unsupportedUsedTileSizes.length > 1) {
      return {
        title: t.get(
          'out_of_stock.we_changed_some_frame_that_werent_available'
        ),
      }
    }

    const unsupportedSize = unsupportedUsedTileSizes[0]
    const sizeUnsupportedForMaterial = !isSupportedSizeForMaterial({
      size: unsupportedUsedTileSizes[0],
      material,
    })

    if (sizeUnsupportedForMaterial) {
      const sizeInStock = getSizesInStockForMaterial({
        materialType: material,
      })[0]

      return {
        title: t.get('out_of_stock.x_isnt_available_size_changed_to_x_title', {
          v1: getSizeName(unsupportedSize),
        }),
        subtitle: t.get(
          'out_of_stock.x_isnt_available_size_changed_to_x_subtitle',
          {
            v1: getSizeName(sizeInStock).toLowerCase(),
          }
        ),
      }
    }

    const colorInStock = getSupportedFramesForSize({
      size: unsupportedUsedTileSizes[0],
      materialType: material,
    })[0]

    return {
      title: t.get('out_of_stock.x_x_isnt_available_color_changed_to_x_title', {
        v1: getSizeName(unsupportedSize),
        v2: getFrameName(
          getTileStyleData(unsupportedTiles[0]).frameColor
        ).toLowerCase(),
      }),
      subtitle: t.get(
        'out_of_stock.x_x_isnt_available_color_changed_to_x_subtitle',
        {
          v1: getFrameName(colorInStock).toLowerCase(),
        }
      ),
    }
  }

  const notifyMaterialInSizeUnavailable = ({
    material,
    prevBulkCustomization,
    unsupportedTiles,
  }) => {
    const unsupportedUsedTileSizes = uniq(
      unsupportedTiles.map((unsupportedTile) => unsupportedTile.tileSize)
    )

    const sizeUnsupportedForMaterial = !isSupportedSizeForMaterial({
      size: unsupportedUsedTileSizes[0],
      material,
    })

    const { title: snackbarTitle, subtitle: snackbarSubtitle } =
      getSnackbarTitles({
        unsupportedTiles,
        unsupportedUsedTileSizes,
        material,
      })

    const unsupportedUsedFrameColors = uniq(
      unsupportedTiles.map(
        (unsupportedTile) => getTileStyleData(unsupportedTile).frameColor
      )
    )

    notifyOutOfStock({
      name: sizeUnsupportedForMaterial
        ? OUT_OF_STOCK_PROPERTY.SIZE
        : OUT_OF_STOCK_PROPERTY.FRAME,
      title: snackbarTitle,
      subtitle: snackbarSubtitle,
      showUndo: true,
      undoCallback: () => {
        return revertMaterial({
          prevBulkCustomization,
          currentMaterial: material,
        })
      },
      type: OUT_OF_STOCK_NOTIFICATION_TYPE.NOT_AVAILABLE,
      missingMaterial: material,
      missingSize:
        unsupportedUsedTileSizes > 1
          ? unsupportedUsedTileSizes
          : unsupportedUsedTileSizes[0],
      missingFrame:
        sizeUnsupportedForMaterial || !isFramedMaterial(material)
          ? 'All'
          : unsupportedUsedFrameColors.length > 1
          ? unsupportedUsedFrameColors
          : unsupportedUsedFrameColors[0],
      previousMaterial: prevBulkCustomization.materialType,
      previousSize: prevBulkCustomization.tileSize,
      previousFrame: prevBulkCustomization.frameColor,
    })
  }

  const revertMaterial = ({ prevBulkCustomization, currentMaterial }) => {
    analytics.track(EVENT_NAMES.UNDO_TAPPED, {
      Type: OUT_OF_STOCK,
      'Previous Material': prevBulkCustomization.materialType,
      'Current Material': currentMaterial,
    })
    updateBulkMaterial(prevBulkCustomization.materialType)
    undoTiles()
    closeSnackbar()
  }

  const getUnsupportedTilesForMaterial = ({ newMaterial }) => {
    return tiles.filter((tile) => {
      return isUnsupportedTileForMaterial({
        tile,
        newMaterial,
      })
    })
  }

  const isUnsupportedTileForMaterial = ({ tile, newMaterial }) => {
    const isCurrentFramedMaterial = isFramedMaterial(tile.materialType)
    const isNewFramedMaterial = isFramedMaterial(newMaterial)

    if (isCenterpiece(tile)) {
      return false
    }

    if (!isCurrentFramedMaterial) {
      return (
        getSupportedFramesForSize({
          size: tile.tileSize,
          materialType: newMaterial,
        }).length === 0
      )
    }

    return !isFrameSupportedForSize({
      frameColor: isNewFramedMaterial
        ? getTileStyleData(tile).frameColor
        : FRAME_COLORS.NONE,
      size: tile.tileSize,
      materialType: newMaterial,
    })
  }

  return {
    bulkMaterial: bulkCustomization.materialType,
    updateBulkMaterial,
    updateTileMaterial,
    supportedMaterials,
  }
}

export default useTileMaterial
